import { useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { filterVideosByRegion } from "../../lib/regions";

/**
 * useFeedPlayback — bundles the playlist-navigation handlers and helpers
 * the FeedPage owns:
 *   - handlePlayAll        → open the player on index 0
 *   - handleSkipNext       → advance one slot, opportunistically fetch new
 *                            videos on wrap-around
 *   - handleVideoEnded     → loop OR auto-advance to the next playable
 *                            index, with new-video re-fetch on wrap
 *   - handleClosePlayer    → tear everything down: stop playback, reset
 *                            index, exit loop mode, drop region filter,
 *                            flip source back to 'all'
 *   - findNextPlayableIndex→ used by the <video> onError handler in the
 *                            persistent overlay so a row of dead URLs is
 *                            skipped silently rather than spamming toasts
 *                            and ejecting the viewer back to the landing.
 *   - toggleLoopMode       → loop-mode toggle
 *
 * Also owns the local UI state that lives alongside the playlist:
 *   - isLoopMode
 *   - showComments       (visibility for the comments side-panel)
 *   - showCreditsPrompt  (which credits modal is open, or null)
 *   - activeRegion       (Global / Americas / Asia / Africa / Europe / Oceania)
 *
 * Lives at /app/frontend/src/pages/feed/useFeedPlayback.js.
 *
 * Inputs:
 *   activeVideos               — current playlist (already region-filtered)
 *   safeVideos                 — full feed (used to compute "new since last
 *                                load" diff on wrap-around)
 *   setVideos                  — feed setter, used when wrap-around fetch
 *                                returns new content
 *   currentVideoIndex, setCurrentVideoIndex — from PlayerContext
 *   setIsPlaying               — from PlayerContext
 *   userPausedRef              — from PlayerContext; cleared on Skip so
 *                                the play-effect actually plays the next
 *                                video
 *   failedIdsRef               — from PlayerContext; used by
 *                                findNextPlayableIndex
 *   exhaustedToastShownRef     — from PlayerContext; reset on Play All so
 *                                the "everything failed" toast re-arms
 *   setSource                  — useFeedSource setter; reset on close so
 *                                next open lands on Global feed
 *
 * Returns: { isLoopMode, setIsLoopMode, showComments, setShowComments,
 *            showCreditsPrompt, setShowCreditsPrompt,
 *            activeRegion, setActiveRegion,
 *            handlePlayAll, handleSkipNext, handleVideoEnded,
 *            handleClosePlayer, findNextPlayableIndex, toggleLoopMode }
 */
export function useFeedPlayback({
  safeVideos,
  setVideos,
  currentVideoIndex, setCurrentVideoIndex,
  setIsPlaying,
  userPausedRef,
  failedIdsRef,
  exhaustedToastShownRef,
  videoRef,
  setSource,
}) {
  const [isLoopMode, setIsLoopMode] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showCreditsPrompt, setShowCreditsPrompt] = useState(null); // null, 'applaud', 'comment', or 'signin'
  const [activeRegion, setActiveRegion] = useState('Global');

  // Compute the region-filtered playlist inside the hook so all the
  // navigation handlers can read it without a circular wiring problem
  // (the page would have needed activeVideos before calling the hook
  // that owns activeRegion).
  const activeVideos = useMemo(
    () => filterVideosByRegion(safeVideos, activeRegion),
    [safeVideos, activeRegion]
  );

  const findNextPlayableIndex = useCallback((startIdx) => {
    if (!activeVideos.length) return null;
    const total = activeVideos.length;
    for (let step = 1; step <= total; step++) {
      const idx = (startIdx + step) % total;
      const v = activeVideos[idx];
      if (v && !failedIdsRef.current.has(v.id)) return idx;
    }
    return null;
  }, [activeVideos, failedIdsRef]);

  const handlePlayAll = useCallback(() => {
    // Always open the dynamic player overlay. Even if the active
    // playlist is empty (e.g. Following feed with no follows), the
    // player now renders an inline empty-state placeholder in the
    // video slot — the toggle stays available so the user can flip
    // to a feed that has content without being kicked back to the
    // landing template.
    exhaustedToastShownRef.current = false;
    setCurrentVideoIndex(0);
    setIsPlaying(true);
    setShowComments(false);
  }, [exhaustedToastShownRef, setCurrentVideoIndex, setIsPlaying]);

  const handleVideoEnded = useCallback(async () => {
    // Honor manual pause — if the user explicitly paused (vs the video
    // playing through naturally), DO NOT auto-advance or loop-replay.
    // The userPausedRef sentinel is set by onPause when there's >0.05s
    // remaining, so a natural end-of-clip pause (currentTime ≈ duration)
    // is correctly NOT flagged and we still auto-advance/loop here.
    if (userPausedRef.current) return;

    if (isLoopMode) {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
      return;
    }

    // Seamless playlist mode: prefer the next PLAYABLE index so we don't
    // immediately re-trigger onError on a known-dead video.
    const nextIdx = findNextPlayableIndex(currentVideoIndex);
    if (nextIdx === null) return; // every video already blacklisted

    // If we're wrapping back to the start, opportunistically check for
    // brand-new videos uploaded since we first loaded the feed.
    const wrapping = nextIdx <= currentVideoIndex;
    if (wrapping) {
      try {
        const res = await api.get(`/videos`);
        const freshVideos = res.data;
        const currentIds = new Set(safeVideos.map(v => v.id));
        const newVideos = freshVideos.filter(v => !currentIds.has(v.id));
        if (newVideos.length > 0) {
          setVideos(freshVideos);
          toast.success(`${newVideos.length} new video${newVideos.length > 1 ? 's' : ''} added!`);
        }
      } catch (err) {
        console.error("Failed to check for new videos:", err);
      }
    }
    setCurrentVideoIndex(nextIdx);
  }, [userPausedRef, isLoopMode, videoRef, findNextPlayableIndex, currentVideoIndex, setCurrentVideoIndex, setVideos, safeVideos]);

  const handleSkipNext = useCallback(async () => {
    // User asked for the next video — clear any prior manual-pause flag
    // so the play-effect actually plays the new video.
    userPausedRef.current = false;
    setIsPlaying(true);
    const isLastVideo = currentVideoIndex >= activeVideos.length - 1;
    if (isLastVideo) {
      try {
        const res = await api.get(`/videos`);
        const freshVideos = res.data;
        const currentIds = new Set(safeVideos.map(v => v.id));
        const newVideos = freshVideos.filter(v => !currentIds.has(v.id));
        if (newVideos.length > 0) {
          setVideos(freshVideos);
          toast.success(`${newVideos.length} new video${newVideos.length > 1 ? 's' : ''} added!`);
        }
      } catch (err) {
        console.error("Failed to check for new videos:", err);
      }
      setCurrentVideoIndex(0);
    } else {
      setCurrentVideoIndex(prevIndex => prevIndex + 1);
    }
  }, [userPausedRef, setIsPlaying, currentVideoIndex, activeVideos.length, setCurrentVideoIndex, setVideos, safeVideos]);

  const handleClosePlayer = useCallback(() => {
    setIsPlaying(false);
    setCurrentVideoIndex(0);
    setIsLoopMode(false);
    setShowComments(false);
    setShowCreditsPrompt(null);
    setActiveRegion('Global');
    // Toggle always defaults back to Global the next time the user
    // re-enters the dynamic screen, per product spec.
    setSource('all');
  }, [setIsPlaying, setCurrentVideoIndex, setSource]);

  const toggleLoopMode = useCallback(() => {
    setIsLoopMode(prev => !prev);
  }, []);

  return {
    activeVideos,
    isLoopMode, setIsLoopMode,
    showComments, setShowComments,
    showCreditsPrompt, setShowCreditsPrompt,
    activeRegion, setActiveRegion,
    handlePlayAll, handleSkipNext, handleVideoEnded,
    handleClosePlayer, findNextPlayableIndex, toggleLoopMode,
  };
}
